import type { SearchSort } from "~/models/polyschema/SearchSort"

import { GlobalComposable } from "~/classes/composable/GlobalComposable"
import { PropertyId } from "~/classes/entity"
import { InterfaceSelection } from "~/classes/entity/InterfaceSelection"
import { StaticFile } from "~/classes/file/StaticFile"
import { sendSearchAutocompleteRequest } from "~/classes/handlers/search/autocomplete"
import { StaticSearch } from "~/classes/search/StaticSearch"

class InstantMenu extends GlobalComposable<{
  visible: boolean
  mode: "search" | "path" | "generate"
  loading: boolean
  query: string
  facets: SearchFacet[]
  sorts: SearchSort[]
  /** If defined, place in the file picker view */
  nearFileSelection?: StaticFile
  nearFileSelectionMenuOpen: boolean
  /** If defined, place in the folder in view */
  folderInSelection?: StaticFile
  folderInSelectionMenuOpen: boolean
  tagSelectionMenusOpen: Record<string, boolean>
  /** If defined, we have a keyboard selection */
  keyboardSelection?: number
  /** If defined, we are in file picker mode */
  filePickerResults?: StaticFile[]
  folderPickerResults?: StaticFile[]
  autocompleteRequests: Set<string>
  autocompleteCached: StaticFile[]
  autocompleteServer: StaticFile[]
  autocompleteServerThrottle?: any
}> {
  #AUTOCOMPLETE_INPUT_THROTTLE_MS = 1000

  override defaultState = () => ({
    visible: false,
    mode: "search" as "search" | "path" | "generate",
    loading: false,
    query: "",
    facets: [],
    sorts: [],
    nearFileSelectionMenuOpen: false,
    folderInSelectionMenuOpen: false,
    autocompleteRequests: new Set<string>(),
    autocompleteCached: [],
    autocompleteServer: [],
    tagSelectionMenusOpen: {},
  })

  override onCreate = () => {
    const { addInterfaceEventListener } = useEvents()
    addInterfaceEventListener("searchAutocompleteResponse", this.onAutocompleteResponse)
  }

  override onDestroy = () => {
    const { removeInterfaceEventListener } = useEvents()
    removeInterfaceEventListener("searchAutocompleteResponse", this.onAutocompleteResponse)
  }

  isInstantMenuOpen = computed(() => this.state.value.visible)

  openInstantMenu = () => {
    this.state.value.loading = false
    this.state.value.visible = true
  }

  closeInstantMenu = () => {
    this.clearState()
  }

  openInstantMenuWithFileNear = (file: StaticFile) => {
    this.clearState()
    this.addFileSimilarityFacet(file)
    this.openInstantMenu()
  }

  openInstantMenuWithFileIn = (folder: StaticFile) => {
    this.clearState()
    this.addFolderInFacet(folder)
    this.openInstantMenu()
  }

  searchFacetType = (facet: SearchFacet): SearchFacetTypeKey => {
    if ("exists" in facet.facetType)
      return "exists"
    else if ("equals" in facet.facetType)
      return "equals"
    else if ("notEquals" in facet.facetType)
      return "notEquals"
    else if ("startsWith" in facet.facetType)
      return "startsWith"
    else if ("contains" in facet.facetType)
      return "contains"
    else if ("fileIn" in facet.facetType)
      return "fileIn"
    else if ("fileOnlyIn" in facet.facetType)
      return "fileOnlyIn"
    else if ("greaterThan" in facet.facetType)
      return "greaterThan"
    else if ("greaterThanEqual" in facet.facetType)
      return "greaterThanEqual"
    else if ("lessThan" in facet.facetType)
      return "lessThan"
    else if ("lessThanEqual" in facet.facetType)
      return "lessThanEqual"
    else if ("afterDate" in facet.facetType)
      return "afterDate"
    else if ("onOrAfterDate" in facet.facetType)
      return "onOrAfterDate"
    else if ("beforeDate" in facet.facetType)
      return "beforeDate"
    else if ("onOrBeforeDate" in facet.facetType)
      return "onOrBeforeDate"
    else if ("nearFile" in facet.facetType)
      return "nearFile"
    else if ("nearImageText" in facet.facetType)
      return "nearImageText"
    else if ("nearAudioText" in facet.facetType)
      return "nearAudioText"
    else if ("nearDocLikeText" in facet.facetType)
      return "nearDocLikeText"
    else if ("nearVideoText" in facet.facetType)
      return "nearVideoText"
    else if ("nearColor" in facet.facetType)
      return "nearColor"
    else if ("hasTag" in facet.facetType)
      return "hasTag"
    else throw new Error(`Unknown facet type: ${facet.facetType}`)
  }

  searchFacetTypeName = (key: SearchFacetTypeKey) => {
    if (key === "exists")
      return "exists"
    else if (key === "equals")
      return "equals"
    else if (key === "notEquals")
      return "is not equal to"
    else if (key === "startsWith")
      return "starts with"
    else if (key === "contains")
      return "contains"
    else if (key === "fileIn")
      return "file in"
    else if (key === "fileOnlyIn")
      return "file only in"
    else if (key === "greaterThan")
      return "is greater than"
    else if (key === "greaterThanEqual")
      return "is greater than or equal to"
    else if (key === "lessThan")
      return "is less than"
    else if (key === "lessThanEqual")
      return "is less than or equal to"
    else if (key === "afterDate")
      return "is after date"
    else if (key === "onOrAfterDate")
      return "is on or after date"
    else if (key === "beforeDate")
      return "is before date"
    else if (key === "onOrBeforeDate")
      return "is on or before date"
    else if (key === "nearFile")
      return "near file"
    else if (key === "nearImageText")
      return "images similar to text"
    else if (key === "nearAudioText")
      return "audio similar to text"
    else if (key === "nearDocLikeText")
      return "documents similar to text"
    else if (key === "nearVideoText")
      return "videos similar to text"
    else if (key === "nearColor")
      return "near color"
    else if (key === "hasTag")
      return "has tag"
    else throw new Error(`Unknown facet type: ${key}`)
  }

  searchFacetValue = (facet: SearchFacet) => {
    if ("exists" in facet.facetType)
      return facet.facetType.exists
    else if ("equals" in facet.facetType)
      return facet.facetType.equals
    else if ("notEquals" in facet.facetType)
      return facet.facetType.notEquals
    else if ("startsWith" in facet.facetType)
      return facet.facetType.startsWith
    else if ("contains" in facet.facetType)
      return facet.facetType.contains
    else if ("fileIn" in facet.facetType)
      return facet.facetType.fileIn
    else if ("fileOnlyIn" in facet.facetType)
      return facet.facetType.fileOnlyIn
    else if ("greaterThan" in facet.facetType)
      return facet.facetType.greaterThan
    else if ("greaterThanEqual" in facet.facetType)
      return facet.facetType.greaterThanEqual
    else if ("lessThan" in facet.facetType)
      return facet.facetType.lessThan
    else if ("lessThanEqual" in facet.facetType)
      return facet.facetType.lessThanEqual
    else if ("afterDate" in facet.facetType)
      return facet.facetType.afterDate
    else if ("onOrAfterDate" in facet.facetType)
      return facet.facetType.onOrAfterDate
    else if ("beforeDate" in facet.facetType)
      return facet.facetType.beforeDate
    else if ("onOrBeforeDate" in facet.facetType)
      return facet.facetType.onOrBeforeDate
    else if ("nearFile" in facet.facetType)
      return facet.facetType.nearFile
    else if ("nearImageText" in facet.facetType)
      return facet.facetType.nearImageText
    else if ("nearAudioText" in facet.facetType)
      return facet.facetType.nearAudioText
    else if ("nearDocLikeText" in facet.facetType)
      return facet.facetType.nearDocLikeText
    else if ("nearVideoText" in facet.facetType)
      return facet.facetType.nearVideoText
    else if ("nearColor" in facet.facetType)
      return facet.facetType.nearColor
    else if ("hasTag" in facet.facetType)
      return facet.facetType.hasTag
    else throw new Error(`Unknown facet type: ${facet.facetType}`)
  }

  makeAutocompleteRequest = (query: string, offline: boolean) => {
    const { state: myself } = useSelf()

    if (!myself.value.home || !myself.value.archive) {
      this.error("Instant menu not supported in anon mode")
      return
    }
    query = query.trim()
    if (!query.length) {
      console.warn("Empty query for autocomplete request")
      return
    }

    // short queries only have the starts-with and longer queries use all facets
    // TODO: is there a way to make this internal to the search?
    // TODO: do more facet types.
    // TODO: add support for 'any text'
    const facets: SearchFacet[] = []
    if (query.length > 2)
      facets.push({ propertyId: PropertyId.FILE_NAME, facetType: { contains: query } })
    else
      facets.push({ propertyId: PropertyId.FILE_NAME, facetType: { startsWith: query } })

    const sorts = this.state.value.sorts
    const requestId = generateWorkerRequestId()
    const request: SearchAutocompleteRequestDatagram = {
      requestId,
      offline,
      fileSelection: InterfaceSelection.minimumAutocompleteSelection().inner(),
      pathSelection: InterfaceSelection.minimumPathSelection().inner(),
      limit: 10,
      locations: this.searchLocations(),
      definition: { facets, sorts },
    }

    sendSearchAutocompleteRequest(request)
    return request
  }

  /**
   * We merge the server-sent and cached results, with server-sent appearing
   * above.
   * TODO: make this a bit more ergonomic since there's a flash in in the UI
   */
  autocompleteResults = computed(() => {
    const results = []
    const fileIds = new Set()
    for (const file of this.state.value.autocompleteServer) {
      if (!fileIds.has(file.id)) {
        results.push(file)
        fileIds.add(file.id)
      }
    }
    for (const file of this.state.value.autocompleteCached) {
      if (!fileIds.has(file.id)) {
        results.push(file)
        fileIds.add(file.id)
      }
    }
    return results
  })

  keyboardSelectedFile = computed(() => {
    if (!isDefined(this.state.value.keyboardSelection))
      return
    // TODO: add file completion results
    return this.autocompleteResults.value[this.state.value.keyboardSelection]
  })

  moveKeyboardSelectionCursor = (direction: "up" | "down") => {
    const maxIndex = this.autocompleteResults.value.length - 1

    if (!this.autocompleteResults.value.length) {
      this.state.value.keyboardSelection = undefined
      return
    }
    else if (!isDefined(this.state.value.keyboardSelection)) {
      if (direction === "down")
        this.state.value.keyboardSelection = 0
      else if (direction === "up")
        this.state.value.keyboardSelection = maxIndex
      return
    }
    else if (this.state.value.keyboardSelection > maxIndex) {
      if (direction === "down")
        this.state.value.keyboardSelection = 0
      else if (direction === "up")
        this.state.value.keyboardSelection = maxIndex
      return
    }
    else if (direction === "down") {
      if (this.state.value.keyboardSelection < maxIndex)
        this.state.value.keyboardSelection += 1
      else
        this.state.value.keyboardSelection = 0
    }
    else if (direction === "up") {
      if (this.state.value.keyboardSelection > 0)
        this.state.value.keyboardSelection -= 1
      else
        this.state.value.keyboardSelection = maxIndex
    }
    else
      throw new Error("Invalid direction for keyboard selection")
  }

  clearKeyboardSelection = () => {
    this.state.value.keyboardSelection = undefined
  }

  /** When the user enters or deletes some text */
  onInput = () => {
    // throttle the autocomplete request to the server, but keep the cached one realtime.
    // On input, check if there's a timeout set, and send cached request if so. Otherwise,
    // send a regular request, and create a timeout for the server one. For the server
    // request, we can also check if the query has changed to decide whether to re-request
    // FIXME: change logic when facet based searches are implemented
    if (isDefined(this.state.value.autocompleteServerThrottle)) {
      const request = this.makeAutocompleteRequest(this.state.value.query, true)
      if (request)
        this.state.value.autocompleteRequests.add(request.requestId)
    }
    else {
      const request = this.makeAutocompleteRequest(this.state.value.query, false) // leading
      if (request)
        this.state.value.autocompleteRequests.add(request.requestId)
      const preTimeoutQuery = this.state.value.query
      this.state.value.autocompleteServerThrottle = setTimeout(() => {
        if (this.state.value.query !== preTimeoutQuery) {
          const request = this.makeAutocompleteRequest(this.state.value.query, false) // trailing
          if (request)
            this.state.value.autocompleteRequests.add(request.requestId)
        }
        delete this.state.value.autocompleteServerThrottle
      }, this.#AUTOCOMPLETE_INPUT_THROTTLE_MS)
    }
  }

  /**
   * TODO: add focus on the facet if it has a focusable element
   */
  addSearchFacet = (facet: SearchFacet) => {
    // can only have one "nearX" facet at a time
    if ("nearColor" in facet.facetType || "nearFile" in facet.facetType)
      this.state.value.facets = this.state.value.facets.filter(
        f => !("nearColor" in f.facetType)
          && !("nearFile" in f.facetType),
      )
    // can only have one "file{Only}In" facet at a time
    if ("fileIn" in facet.facetType || "fileOnlyIn" in facet.facetType)
      this.state.value.facets = this.state.value.facets.filter(
        f => !("fileIn" in f.facetType)
          && !("fileOnlyIn" in f.facetType),
      )
    this.state.value.facets.push(facet)
  }

  addSearchSort = (sort: SearchSort) => {
    // remove other sorts on the same property
    this.state.value.sorts = this.state.value.sorts.filter(s => s.propertyId !== sort.propertyId)
    // add the new sort
    this.state.value.sorts.push(sort)
  }

  addFileSimilarityFacet = (file: StaticFile) => {
    this.addSearchFacet({ propertyId: PropertyId.FILE_VIRTUAL_EMBEDDINGS, facetType: { nearFile: file.id } })
    this.state.value.nearFileSelection = file
  }

  addFolderInFacet = (folder: StaticFile) => {
    this.addSearchFacet({ propertyId: PropertyId.FILE_VIRTUAL_PATH, facetType: { fileIn: folder.id } })
    this.state.value.folderInSelection = folder
  }

  addFolderOnlyInFacet = (folder: StaticFile) => {
    this.addSearchFacet({ propertyId: PropertyId.FILE_PARENT_ID, facetType: { fileOnlyIn: folder.id } })
    this.state.value.folderInSelection = folder
  }

  /** When the user initiates a search */
  onSearch = async () => {
    const { state: myself } = useSelf()
    const { navigateToSearch } = useNavigation()
    const { createSearch } = useSearch()

    if (!myself.value.home || !myself.value.archive)
      return this.error("Instant menu not supported in anon mode")

    const query = this.state.value.query.trim()
    const facets = [...this.state.value.facets]

    // our "theme" search only adds the DocLikeText facet, but we want to add the
    // image and video facet as well to augment the query.
    const docLikeFacet = facets.find(f => "nearDocLikeText" in f.facetType)
    if (isDefined(docLikeFacet) && ("nearDocLikeText" in docLikeFacet.facetType)) {
      facets.push({
        propertyId: PropertyId.FILE_VIRTUAL_EMBEDDINGS,
        facetType: { nearImageText: docLikeFacet.facetType.nearDocLikeText },
      })
      facets.push({
        propertyId: PropertyId.FILE_VIRTUAL_EMBEDDINGS,
        facetType: { nearVideoText: docLikeFacet.facetType.nearDocLikeText },
      })
    }

    // short queries only have the starts-with and longer queries use all facets
    // TODO: is there a way to make this internal to the search?
    // TODO: do more facet types.
    // TODO: add support for 'any text'
    if (query.length > 2)
      facets.push({ propertyId: PropertyId.FILE_NAME, facetType: { contains: query } })
    else if (query.length)
      facets.push({ propertyId: PropertyId.FILE_NAME, facetType: { startsWith: query } })

    const sorts = this.state.value.sorts
    const definition = { facets, sorts }

    const request = await createSearch({
      locations: this.searchLocations(),
      definition,
      pageSize: 50,
      limit: 100,
      offline: false,
    })

    // show a loading indicator until we navigate to the page
    this.state.value.loading = true

    await navigateToSearch(new StaticSearch(request.search), true)
    this.closeInstantMenu()
  }

  onAutocompleteResponse = (response: SearchAutocompleteResponseDatagram) => {
    if (!this.state.value.autocompleteRequests.has(response.requestId))
      return console.warn("Unknown autocomplete response", response.requestId)

    if (response.error || !response.files)
      return this.warn("Response errored", response)

    // TODO: merge cached result file if it is there, rather than adding server result

    const files = response.files.map(file => new StaticFile(file))
    if (response.offline)
      this.state.value.autocompleteCached = files
    else
      this.state.value.autocompleteServer = files
  }

  /**
   * The default search locations are the user's personal home and the home folders
   * of all the user's shared folders.
   */
  searchLocations = () => {
    const { state: myself } = useSelf()
    const { state: sharedFolderState } = useSharedFolders()

    // if the user selected In/OnlyIn, that folder's root is the only location
    if (isDefined(this.state.value.folderInSelection)) {
      const root = this.state.value.folderInSelection.rootFolder()
      if (!isDefined(root))
        throw new Error("No root folder for folder in selection")
      return [root.id]
    }

    if (!myself.value.home)
      throw new Error("Instant menu not supported in anon mode")

    const locations = [myself.value.home.id]
    for (const { home } of Object.values(sharedFolderState.value.all))
      locations.push(home.id)

    return locations
  }

  matchingTags = async (query: string): Promise<string[]> => {
    const { cacheWorker } = useCache()
    const { findMatchingTags } = useTauri()

    let tags: string[] = []
    if (isDesktop())
      tags = await findMatchingTags(query)
    else
      tags = await cacheWorker().findMatchingTags(query)

    return tags
  }
}

const instantMenu = new InstantMenu("instant-menu", theme.colors.teal.hex)
export default function () {
  return instantMenu.use()
}
